import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { generateFullBackupReceiptHtml, downloadReceiptPdf } from '../utils/receiptPdfGenerator';
import { apiUrl } from '../api';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboardHome = location.pathname === '/dashboard';
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isBackingUp, setIsBackingUp] = React.useState(false);

  // Live Real-Time Incoming Purchase Alert
  const [incomingBill, setIncomingBill] = useState(null);
  const [viewingIncomingBill, setViewingIncomingBill] = useState(null);
  const knownPurchasesCountRef = useRef(null);

  // Poll for new purchase bills across all devices/PCs every 6 seconds
  useEffect(() => {
    let isMounted = true;

    const checkIncomingPurchases = async () => {
      try {
        const res = await fetch(apiUrl('/api/purchases'));
        if (!res.ok) return;
        const purchases = await res.json();
        
        if (isMounted && Array.isArray(purchases) && purchases.length > 0) {
          const currentCount = purchases.length;
          
          if (knownPurchasesCountRef.current !== null && currentCount > knownPurchasesCountRef.current) {
            // A new purchase was added on some PC!
            const latestBill = purchases[0]; // newest is first
            setIncomingBill(latestBill);

            // Auto-hide popup after 12 seconds if not interacted with
            setTimeout(() => {
              if (isMounted) setIncomingBill(prev => prev && prev.id === latestBill.id ? null : prev);
            }, 12000);
          }
          knownPurchasesCountRef.current = currentCount;
        }
      } catch (err) {
        // silent fail on network glitch
      }
    };

    checkIncomingPurchases();
    const interval = setInterval(checkIncomingPurchases, 6000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    navigate('/');
  };

  const handleBackup = async () => {
    try {
      setIsBackingUp(true);
      const userAgent = navigator.userAgent || 'Web Browser';
      const isWindows = userAgent.includes('Windows') ? 'Windows PC' : userAgent.includes('Mac') ? 'Mac' : 'Browser / Device';
      
      const payload = {
        notes: 'One-Click Dashboard Cloud Backup',
        clientDevice: `${isWindows} (${window.location.hostname})`
      };

      const res = await fetch(apiUrl('/api/backups'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Failed to create cloud backup");
      const data = await res.json();

      // Trigger download for authentic Receipt PDF
      if (data.fullData) {
        const dateStr = new Date().toISOString().split('T')[0];
        const receiptHtml = generateFullBackupReceiptHtml(data.fullData);
        await downloadReceiptPdf(receiptHtml, `MediFlow_Receipt_Backup_${dateStr}_${Date.now().toString().slice(-4)}.pdf`);
      }

      alert("Cloud Backup completed successfully! Medical Store Receipt (PDF) has been downloaded.");
      navigate('/dashboard/backups');
    } catch (error) {
      console.error("Error backing up data:", error);
      alert("Failed to backup data: " + error.message);
    } finally {
      setIsBackingUp(false);
    }
  };


  // Navigation items configuration
  const navItems = useMemo(() => [
    { path: '/dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/dashboard/sales', label: 'Sales', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { path: '/dashboard/sales-return', label: 'Sales Return', icon: 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6' },
    { path: '/dashboard/purchases', label: 'Purchases', icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' },
    { path: '/dashboard/stockist-purchases', label: 'Stockist Purchases', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { path: '/dashboard/daily-report', label: 'Daily Report', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { path: '/dashboard/ledger-report', label: 'Ledger', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { path: '/dashboard/near-expiry', label: 'Near Expiry', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { path: '/dashboard/backups', label: 'Cloud Backups', icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' },
  ], []);

  // Selection index for Arrow Navigation
  const [focusedIndex, setFocusedIndex] = useState(() => {
    const idx = navItems.findIndex(item => item.path === location.pathname);
    return idx !== -1 ? idx : 0;
  });

  // Sync focusedIndex when route changes
  useEffect(() => {
    const idx = navItems.findIndex(item => item.path === location.pathname);
    if (idx !== -1) {
      setFocusedIndex(idx);
    }
  }, [location.pathname, navItems]);

  // Global Keyboard Navigation (Esc to return to Dashboard, ArrowUp/ArrowDown for Sidebar)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 1. ESC Key: Return to main Dashboard page from any sub-page (Sales, Purchases, etc.)
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (location.pathname !== '/dashboard') {
          e.preventDefault();
          navigate('/dashboard');
          return;
        }
      }

      // Don't intercept if user is typing in an input, textarea, select, or editable element
      const activeElement = document.activeElement;
      if (activeElement) {
        const tagName = activeElement.tagName ? activeElement.tagName.toLowerCase() : '';
        if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || activeElement.isContentEditable) {
          return;
        }
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % navItems.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + navItems.length) % navItems.length);
      } else if (e.key === 'Enter') {
        const activeTag = activeElement ? activeElement.tagName.toLowerCase() : '';
        if (activeTag === 'button' || activeTag === 'a') return;

        e.preventDefault();
        if (navItems[focusedIndex]) {
          navigate(navItems[focusedIndex].path);
          setMobileMenuOpen(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedIndex, location.pathname, navigate, navItems]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex font-sans overflow-hidden">
      
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur-lg border-b border-slate-200/50 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <span className="text-xl font-bold text-slate-800 tracking-tight">MediFlow</span>
        </div>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
          </svg>
        </button>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden animate-in fade-in duration-200" 
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} ${isDashboardHome ? 'lg:relative lg:translate-x-0' : 'lg:hidden'} transition-all duration-300 cubic-bezier(0.4, 0, 0.2, 1) w-64 flex-shrink-0 bg-[#0A0F1C] border-r border-white/5 text-slate-300 flex flex-col z-50 shadow-[4px_0_24px_rgba(0,0,0,0.4)]`}>
        
        {/* Logo Section */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-white/5 shrink-0 bg-[#0A0F1C] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -mr-10 -mt-10"></div>
          <div className="flex items-center gap-3 group cursor-pointer relative z-10">
            <div className="w-10 h-10 rounded-[1rem] bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.3)] group-hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] group-hover:scale-105 transition-all duration-500 relative overflow-hidden">
              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"></div>
              <svg className="w-5 h-5 text-white relative z-10 drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl font-black tracking-tight text-white drop-shadow-sm group-hover:text-emerald-50 transition-colors">MediFlow</h1>
              <p className="text-[10px] text-emerald-400/80 font-bold tracking-[0.25em] uppercase mt-0.5">Pharmacy</p>
            </div>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 min-h-0 px-4 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
          <div className="space-y-1">
            {navItems.map((item, idx) => {
              const isFocused = idx === focusedIndex;
              return (
                <NavLink 
                  key={item.path}
                  to={item.path} 
                  end={item.path === '/dashboard'}
                  onClick={() => {
                    setFocusedIndex(idx);
                    setMobileMenuOpen(false);
                  }} 
                  onMouseEnter={() => setFocusedIndex(idx)}
                  className={({ isActive }) => `
                    group relative flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-300 font-black text-xs overflow-hidden border
                    ${isFocused
                      ? 'text-white bg-gradient-to-r from-emerald-500/30 via-teal-500/20 to-emerald-600/30 border-emerald-400 shadow-[0_0_18px_rgba(16,185,129,0.35)] ring-2 ring-emerald-400 scale-[1.01]' 
                      : isActive 
                        ? 'text-white bg-gradient-to-r from-emerald-500/20 to-teal-500/5 border-emerald-500/30 shadow-[inset_0_0_20px_rgba(16,185,129,0.1)]' 
                        : 'text-slate-400 border-transparent hover:text-white hover:bg-white/5 hover:border-white/10'
                    }
                  `}
                >
                  {({ isActive }) => (
                    <>
                      {(isActive || isFocused) && (
                        <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-6 rounded-r-full shadow-[0_0_15px_rgba(16,185,129,1)] ${isFocused ? 'bg-emerald-400 animate-pulse' : 'bg-emerald-500'}`}></div>
                      )}
                      
                      <div className={`p-1 rounded-lg transition-all duration-300 ${isFocused ? 'bg-emerald-400 text-slate-950 shadow-md font-extrabold' : isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800/50 text-slate-500 group-hover:bg-slate-700/50 group-hover:text-slate-300'}`}>
                        <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={isActive || isFocused ? "2.5" : "2"} d={item.icon} />
                        </svg>
                      </div>
                      
                      <span className={`transition-all duration-300 group-hover:translate-x-1 ${isActive || isFocused ? 'font-bold tracking-wide text-white' : ''}`}>{item.label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* User & Quick Actions Section */}
        <div className="p-3.5 border-t border-white/10 shrink-0 bg-[#080D1A] space-y-2.5">
          <div className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 cursor-pointer group shadow-md">
            <div className="relative">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-black text-sm shadow-[0_0_15px_rgba(16,185,129,0.4)] group-hover:scale-105 transition-transform">
                SP
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#080D1A]"></div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-white truncate group-hover:text-emerald-400 transition-colors">Sai Pharmacy</p>
              <p className="text-[10px] text-emerald-400/90 font-black tracking-wider uppercase">Administrator</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={handleBackup} 
              disabled={isBackingUp}
              className="group flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500 text-emerald-300 hover:text-white rounded-xl transition-all duration-300 font-extrabold text-xs border border-emerald-500/40 shadow-sm hover:shadow-[0_0_15px_rgba(16,185,129,0.4)] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Cloud Backup Today's Data"
            >
              {isBackingUp ? (
                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              )}
              <span>Backup</span>
            </button>
            
            <button 
              onClick={handleLogout} 
              className="group flex items-center justify-center gap-1.5 px-3 py-2 bg-rose-500/20 hover:bg-rose-600 text-rose-300 hover:text-white rounded-xl transition-all duration-300 font-extrabold text-xs border border-rose-500/40 shadow-sm hover:shadow-[0_0_15px_rgba(244,63,94,0.4)] active:scale-95"
              title="Logout Account"
            >
              <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden min-h-screen lg:h-screen pt-16 lg:pt-0 flex flex-col">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500 shrink-0" />
        
        {/* Top Header Controls: Back Button & Global Refresh Button */}
        <div className="px-3 py-1.5 flex items-center justify-between shrink-0 bg-white/60 border-b border-slate-200/60 print:hidden">
          {!isDashboardHome ? (
            <NavLink 
              to="/dashboard" 
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-emerald-600 transition-colors group"
            >
              <svg className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Overview
            </NavLink>
          ) : (
            <div className="text-[11px] font-bold text-slate-400">Sai Pharmacy • Live Medical Store Management</div>
          )}

          {/* Global Refresh Button */}
          <button 
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-white hover:bg-slate-50 text-slate-700 hover:text-emerald-700 border border-slate-200/90 hover:border-emerald-300 rounded-xl text-xs font-extrabold shadow-2xs transition-all active:scale-95 cursor-pointer group"
            title="Reload live data for current page"
          >
            <svg className="w-3.5 h-3.5 text-emerald-600 group-hover:rotate-180 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh</span>
          </button>
        </div>

        {/* Page Content */}
        <div className="p-1 sm:p-2 flex-1 overflow-y-auto flex flex-col">
          <Outlet />
        </div>

        {/* Live Floating Incoming Purchase Bill Notification Toast */}
        {incomingBill && (
          <div className="fixed bottom-5 right-5 z-[250] max-w-sm w-full bg-white border-2 border-emerald-500 rounded-2xl shadow-2xl p-4 animate-in slide-in-from-bottom-5 duration-300">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-lg shrink-0 animate-bounce">
                🔔
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-black tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    New Purchase Inward!
                  </span>
                  <button 
                    onClick={() => setIncomingBill(null)} 
                    className="text-slate-400 hover:text-slate-600 font-bold text-sm"
                  >
                    ×
                  </button>
                </div>
                <h4 className="text-xs font-black text-slate-800 uppercase mt-1 truncate">
                  {incomingBill.supplierName || 'Unknown Party'}
                </h4>
                <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                  Inv #{incomingBill.invoiceNo || '-'} • <strong className="text-slate-900 font-black">₹{parseFloat(incomingBill.netAmount || incomingBill.totalAmount || incomingBill.grossAmount || 0).toFixed(2)}</strong>
                </p>
                <div className="mt-2.5 flex items-center gap-2">
                  <button
                    onClick={() => {
                      setViewingIncomingBill(incomingBill);
                      setIncomingBill(null);
                    }}
                    className="flex-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs shadow-sm transition-all text-center"
                  >
                    View Bill
                  </button>
                  <button
                    onClick={() => {
                      setIncomingBill(null);
                      navigate('/dashboard/stockist-purchases');
                    }}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs transition-all"
                  >
                    All Bills
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Incoming Bill Details Modal */}
        {viewingIncomingBill && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[300] flex items-center justify-center p-3 md:p-6 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-3xl max-h-[85vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
              <div className="bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-900 text-white p-5 flex justify-between items-start shrink-0">
                <div>
                  <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider">
                    Incoming Stockist Purchase
                  </span>
                  <h3 className="text-xl font-black mt-1 uppercase tracking-tight">
                    {viewingIncomingBill.supplierName || 'Unknown Party'}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-blue-100 font-medium">
                    <span>📄 Inv: <strong className="text-white">{viewingIncomingBill.invoiceNo || '-'}</strong></span>
                    <span>•</span>
                    <span>📅 Date: <strong className="text-white">{viewingIncomingBill.date || '-'}</strong></span>
                    <span>•</span>
                    <span>💳 Payment: <strong className="text-white">{viewingIncomingBill.paymentType || 'Credit'}</strong></span>
                  </div>
                </div>
                <button 
                  onClick={() => setViewingIncomingBill(null)} 
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center font-bold text-base"
                >
                  ×
                </button>
              </div>

              <div className="flex-1 overflow-auto p-4 bg-slate-50">
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="p-3 bg-slate-100 border-b border-slate-200 font-bold text-xs text-slate-700 uppercase">
                    Purchased Medicines ({viewingIncomingBill.items?.length || 0})
                  </div>
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold border-b border-slate-200">
                        <th className="p-2.5">Medicine Name</th>
                        <th className="p-2.5">Batch</th>
                        <th className="p-2.5">Exp</th>
                        <th className="p-2.5 text-center">Qty</th>
                        <th className="p-2.5 text-right">Rate (₹)</th>
                        <th className="p-2.5 text-right">MRP (₹)</th>
                        <th className="p-2.5 text-right">Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(viewingIncomingBill.items || []).map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-2.5 font-bold text-slate-800 uppercase">{item.productName}</td>
                          <td className="p-2.5 font-semibold text-slate-600">{item.batch || '-'}</td>
                          <td className="p-2.5 text-slate-600">{item.exp || '-'}</td>
                          <td className="p-2.5 text-center font-bold text-blue-600 bg-blue-50/30">{item.qty || 0}</td>
                          <td className="p-2.5 text-right text-slate-700 font-semibold">₹{parseFloat(item.rate || 0).toFixed(2)}</td>
                          <td className="p-2.5 text-right text-slate-500">₹{parseFloat(item.mrp || 0).toFixed(2)}</td>
                          <td className="p-2.5 text-right font-black text-slate-900">
                            ₹{parseFloat(item.amount || ((parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0))).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 p-4 bg-white rounded-2xl border border-slate-200 flex justify-between items-center">
                  <div className="text-xs font-bold text-slate-500 uppercase">Total Bill Amount</div>
                  <div className="text-2xl font-black text-blue-700">
                    ₹{parseFloat(viewingIncomingBill.netAmount || viewingIncomingBill.totalAmount || viewingIncomingBill.grossAmount || 0).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white border-t border-slate-200 flex justify-between items-center shrink-0">
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl font-bold text-xs transition-colors flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                  Print Bill
                </button>
                <button
                  onClick={() => setViewingIncomingBill(null)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs transition-colors shadow-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardLayout;
